import { useEffect, useRef, useCallback, useState } from 'react'
import * as joint from '@joint/core'
import { downloadSVG, downloadPNG } from '../lib/jointjs'

interface C4DiagramCanvasProps {
  graph: joint.dia.Graph
  width?: number | string
  height?: number | string
  onElementClick?: (elementId: string) => void
  onElementSelect?: (elementId: string | null) => void
  diagramName?: string
}

/**
 * JointJS Paper wrapper component for rendering C4 diagrams
 */
export default function C4DiagramCanvas({
  graph,
  width = '100%',
  height = '100%',
  onElementClick,
  onElementSelect,
  diagramName = 'diagram',
}: C4DiagramCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const paperRef = useRef<joint.dia.Paper | null>(null)
  const [scale, setScale] = useState(1)
  const [_selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Initialize paper
  useEffect(() => {
    if (!containerRef.current) return

    const paper = new joint.dia.Paper({
      el: containerRef.current,
      model: graph,
      width: typeof width === 'number' ? width : '100%',
      height: typeof height === 'number' ? height : '100%',
      gridSize: 10,
      drawGrid: false,
      background: {
        color: '#ffffff',
      },
      interactive: {
        elementMove: true,
        linkMove: false,
        labelMove: false,
      },
      defaultLink: () => new joint.shapes.standard.Link(),
      validateConnection: () => false, // Disable new connections
    })

    // Handle element clicks
    paper.on('element:pointerclick', (elementView) => {
      const element = elementView.model
      const elementId = element.id as string
      setSelectedElementId(elementId)
      onElementClick?.(elementId)
      onElementSelect?.(elementId)
    })

    // Handle blank click to deselect
    paper.on('blank:pointerclick', () => {
      setSelectedElementId(null)
      onElementSelect?.(null)
    })

    // Enable panning
    let isPanning = false
    let panStartPosition = { x: 0, y: 0 }

    paper.on('blank:pointerdown', (evt: joint.dia.Event) => {
      isPanning = true
      const originalEvent = evt.originalEvent as MouseEvent
      panStartPosition = {
        x: originalEvent.clientX,
        y: originalEvent.clientY,
      }
      containerRef.current!.style.cursor = 'grabbing'
    })

    const handleMouseMove = (evt: MouseEvent) => {
      if (!isPanning) return

      const dx = evt.clientX - panStartPosition.x
      const dy = evt.clientY - panStartPosition.y

      const translate = paper.translate()
      paper.translate(translate.tx + dx, translate.ty + dy)

      panStartPosition = {
        x: evt.clientX,
        y: evt.clientY,
      }
    }

    const handleMouseUp = () => {
      if (isPanning) {
        isPanning = false
        containerRef.current!.style.cursor = 'default'
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    paperRef.current = paper

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Don't call paper.remove() as it removes the container element
      // Just clear the container's content
      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
      paperRef.current = null
    }
  }, [graph, width, height, onElementClick])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (!paperRef.current) return
    const newScale = Math.min(scale * 1.2, 3)
    setScale(newScale)
    paperRef.current.scale(newScale)
  }, [scale])

  const handleZoomOut = useCallback(() => {
    if (!paperRef.current) return
    const newScale = Math.max(scale / 1.2, 0.3)
    setScale(newScale)
    paperRef.current.scale(newScale)
  }, [scale])

  const handleZoomReset = useCallback(() => {
    if (!paperRef.current) return
    setScale(1)
    paperRef.current.scale(1)
    paperRef.current.translate(0, 0)
  }, [])

  const handleFitContent = useCallback(() => {
    if (!paperRef.current || !containerRef.current) return

    const bbox = graph.getBBox()
    if (!bbox) return

    const containerWidth = containerRef.current.clientWidth
    const containerHeight = containerRef.current.clientHeight

    const padding = 50
    const scaleX = (containerWidth - padding * 2) / bbox.width
    const scaleY = (containerHeight - padding * 2) / bbox.height
    const newScale = Math.min(scaleX, scaleY, 1)

    setScale(newScale)
    paperRef.current.scale(newScale)

    // Center the content
    const scaledWidth = bbox.width * newScale
    const scaledHeight = bbox.height * newScale
    const tx = (containerWidth - scaledWidth) / 2 - bbox.x * newScale
    const ty = (containerHeight - scaledHeight) / 2 - bbox.y * newScale
    paperRef.current.translate(tx, ty)
  }, [graph])

  // Fit content on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      handleFitContent()
    }, 100)
    return () => clearTimeout(timer)
  }, [handleFitContent])

  // Export handlers
  const handleExportSVG = useCallback(() => {
    if (!paperRef.current) return
    const filename = `${diagramName.replace(/\s+/g, '-').toLowerCase()}.svg`
    downloadSVG(paperRef.current, filename)
  }, [diagramName])

  const handleExportPNG = useCallback(async () => {
    if (!paperRef.current) return
    setIsExporting(true)
    try {
      const filename = `${diagramName.replace(/\s+/g, '-').toLowerCase()}.png`
      await downloadPNG(paperRef.current, filename)
    } catch (error) {
      console.error('Failed to export PNG:', error)
    } finally {
      setIsExporting(false)
    }
  }, [diagramName])

  return (
    <div className="relative h-full w-full">
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col space-y-1 bg-white rounded shadow-md border border-gray-200">
        <button
          onClick={handleZoomIn}
          className="p-2 hover:bg-gray-100 rounded-t text-gray-600"
          title="Zoom In"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
          </svg>
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 hover:bg-gray-100 text-gray-600"
          title="Zoom Out"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
          </svg>
        </button>
        <button
          onClick={handleZoomReset}
          className="p-2 hover:bg-gray-100 text-gray-600 text-xs font-medium"
          title="Reset Zoom"
        >
          1:1
        </button>
        <button
          onClick={handleFitContent}
          className="p-2 hover:bg-gray-100 text-gray-600"
          title="Fit Content"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>
        <div className="border-t border-gray-200" />
        <button
          onClick={handleExportPNG}
          disabled={isExporting}
          className="p-2 hover:bg-gray-100 text-gray-600 disabled:opacity-50"
          title="Export as PNG"
        >
          {isExporting ? (
            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </button>
        <button
          onClick={handleExportSVG}
          className="p-2 hover:bg-gray-100 rounded-b text-gray-600"
          title="Export as SVG"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      {/* Scale indicator */}
      <div className="absolute bottom-2 right-2 z-10 bg-white px-2 py-1 rounded text-xs text-gray-500 shadow border border-gray-200">
        {Math.round(scale * 100)}%
      </div>

      {/* Canvas container */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-hidden"
        style={{ cursor: 'default' }}
      />
    </div>
  )
}
